import { useEffect, useRef } from 'react';

/**
 * This is a custom React Hook that provides a declarative useEventListener.
 * Borrowed from here (MIT Licensed):
 * https://github.com/donavon/use-event-listener/blob/46471cd5fb77f9f06ce21995ca86205991b56b60/src/index.js
 *
 * @param eventName - name of the event to use
 * @param handler - function handler
 * @param element - element to listen on @default window
 * @example
 *  const [isFilterOpen, toggleIsFilterOpen] = useToggle(false);
 *  const handleClick = () => {
 *    if (isFilterOpen) {
 *      toggleIsFilterOpen();
 *    }
 *  };
 *  useEventListener('click', handleClick);
 */
const useEventListener = (eventName: string, handler: EventListener, element = window) => {
  // Create a ref that stores handler
  const savedHandler = useRef<EventListener>();

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(
    () => {
      // Make sure element supports addEventListener
      const isSupported = element && element.addEventListener;
      if (!isSupported) {
        return;
      }

      // Create event listener that calls handler function stored in ref
      const eventListener = (event: Event) => savedHandler?.current?.(event);

      // Add event listener
      element.addEventListener(eventName, eventListener);

      // Remove event listener on cleanup
      // eslint-disable-next-line consistent-return
      return () => {
        element.removeEventListener(eventName, eventListener);
      };
    },
    [eventName, element], // Re-run if eventName or element changes
  );
};

export default useEventListener;
